<template>
    <div :class="wrapperClass">
        <button
            v-for="option in options"
            :key="option.value"
            type="button"
            :class="optionClass(option.value)"
            :disabled="disabled || option.disabled"
            @click="select(option.value)"
        >
            <fa v-if="option.icon" :icon="option.icon" class="mr-1" />
            <t v-if="option.labelPath" :path="option.labelPath" />
            <span v-else>{{ option.label }}</span>
        </button>
    </div>

    <p class="small text-primary-300 mt-1" v-if="footer">
        <t :path="footer" />
    </p>
</template>

<script setup lang="ts">
import type { ClassValue } from 'clsx';
import { computed } from 'vue';
import { cn } from '~/lib/utils';

export interface SegmentedControlOption {
    value: string;
    label?: string;
    labelPath?: string;
    icon?: string[];
    disabled?: boolean;
}

const props = withDefaults(
    defineProps<{
        modelValue?: string | null;
        options: SegmentedControlOption[];
        disabled?: boolean;
        stackOnMobile?: boolean;
        size?: 'sm' | 'md';
        class?: ClassValue;
        footer?: string;
    }>(),
    {
        modelValue: null,
        size: 'sm',
    },
);

const emit = defineEmits<{
    'update:modelValue': [value: string];
    change: [value: string];
}>();

const wrapperClass = computed(() =>
    cn(
        'bg-primary-500/40 inline-flex flex-wrap gap-1 rounded-lg p-1',
        props.stackOnMobile ? 'flex w-full flex-col sm:inline-flex sm:w-auto sm:flex-row sm:flex-wrap' : null,
        props.class,
    ),
);

const optionClass = (value: string): string =>
    cn(
        'hover:bg-primary-500/50 hover:text-primary-50/80 cursor-pointer rounded-md border-2 border-solid border-transparent text-center text-sm shadow-none transition-colors disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-40',
        props.size === 'sm' ? 'px-3 py-1 text-xs' : 'px-3 py-2',
        props.stackOnMobile ? 'w-full whitespace-normal break-words sm:w-auto sm:whitespace-nowrap' : null,
        props.modelValue === value ? 'bg-primary-500 text-accent-500' : 'bg-primary-500 opacity-65',
    );

const select = (value: string): void => {
    if (props.disabled) {
        return;
    }

    emit('update:modelValue', value);
    emit('change', value);
};
</script>
